import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { spring, useUITheme, ThemeToggle, pageTheme } from "@/components/ios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TITLES = { signin: "Sign in to Doclyx.", signup: "Create your account.", forgot: "Reset your password." };

export default function Login() {
  const [params] = useSearchParams();
  const requestedNext = params.get("next");
  const next = requestedNext && requestedNext.startsWith("/") ? requestedNext : "/account";
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { dark, toggle } = useUITheme();
  const t = pageTheme(dark);

  const inputCls = `w-full min-h-[48px] rounded-sm border border-transparent px-4 text-[16px] focus:outline-none focus:ring-2 transition-[background-color,box-shadow] duration-200 ${t.input}`;

  const switchMode = (m) => {
    setMode(m);
    setError(null);
    setNotice(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      if (mode === "forgot") {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email, origin_url: window.location.origin });
        setNotice(data.message);
      } else if (mode === "signup") {
        await register(email, password, name);
        navigate(next);
      } else {
        await login(email, password);
        navigate(next);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className={`min-h-screen flex items-center justify-center px-5 py-10 font-sans ${t.page}`} data-testid="auth-page" data-theme={dark ? "dark" : "light"}>
      <div className="fixed top-5 right-5 z-40">
        <ThemeToggle dark={dark} onToggle={toggle} testid="auth-theme-toggle" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={spring}
        className={`rounded-[32px] p-8 md:p-12 max-w-md w-full ${t.card}`}
      >
        <div className="mb-8"><Logo /></div>
        <h1 className={`font-bold tracking-tight text-2xl md:text-[28px] mb-7 ${t.primary}`} data-testid="auth-title">{TITLES[mode]}</h1>

        {notice ? (
          <p className={`text-[15px] font-medium ${t.link}`} data-testid="auth-notice">{notice}</p>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "signup" && (
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name"
                data-testid="auth-name-input"
                className={inputCls}
              />
            )}
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              data-testid="auth-email-input"
              className={inputCls}
            />
            {mode !== "forgot" && (
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (8+ characters)"
                data-testid="auth-password-input"
                className={inputCls}
              />
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-button"
              className={`w-full min-h-[48px] rounded-full ${t.primaryBtn} text-[15px] font-semibold flex items-center justify-center gap-2 disabled:opacity-40 active:scale-[0.98] transition-transform duration-150`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : (
                <>{mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"} <ArrowRight className="w-4 h-4" strokeWidth={2} /></>
              )}
            </button>
            {error && <p className="text-[13px] font-medium text-[#FF3B30]" data-testid="auth-error">{error}</p>}
          </form>
        )}

        <div className="mt-7 space-y-2.5 text-[14px]">
          {mode !== "signin" && (
            <button onClick={() => switchMode("signin")} data-testid="auth-toggle-signin" className={`block ${t.link} min-h-[32px]`}>
              Already have an account? Sign in
            </button>
          )}
          {mode !== "signup" && (
            <button onClick={() => switchMode("signup")} data-testid="auth-toggle-signup" className={`block ${t.link} min-h-[32px]`}>
              New here? Create an account
            </button>
          )}
          {mode === "signin" && (
            <button onClick={() => switchMode("forgot")} data-testid="auth-forgot-link" className={`block ${t.secondary} min-h-[32px]`}>
              Forgot your password?
            </button>
          )}
        </div>

        <Link to="/" data-testid="auth-back-link"
          className={`inline-flex items-center gap-2 text-[14px] ${t.linkMuted} transition-colors duration-200 mt-8 min-h-[44px]`}>
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to Doclyx
        </Link>
      </motion.div>
    </main>
  );
}
